import { createSelector } from 'reselect';
import { VIEW_ALL_OPTION_ID, FEATURED_OPTION_ID } from 'src/constants/accessories';
import sortOptions from 'src/config/sortOptions.config';

export const currentStepSelector = createSelector(
  state => state.configurator.step,
  step => step
);

export const openModalSelector = createSelector(
  state => state.configurator.openModals,
  openModals => openModals
);

export const isModalOpenSelector = createSelector(
  state => state.configurator.openModals,
  openModals => Object.values(openModals).some(isOpen => isOpen)
);

export const modalPropsSelector = modalName =>
  createSelector(
    state => state.configurator.modalProps[modalName],
    modalProps => modalProps
  );

export const disclaimerTypeSelector = createSelector(
  state => state.configurator.disclaimer.type,
  disclaimerType => disclaimerType
);

export const disclaimerOpenSelector = createSelector(
  state => state.configurator.disclaimer.open,
  disclaimerOpen => disclaimerOpen
);

export const accessoryCategoriesSelector = createSelector(
  state => state.configurator.accessories.categories,
  categories => categories
);

export const accessoryItemsSelector = createSelector(
  state => state.configurator.accessories.items,
  items => items
);

export const accessoryExtrasSelector = createSelector(
  state => state.configurator.accessories.extras,
  extras => extras
);

export const accessoryCategoryIdSelector = createSelector(
  state => state.configurator.accessories.category,
  selectedCategory => selectedCategory
);

export const accessoryCategorySelector = createSelector(
  accessoryCategoryIdSelector,
  accessoryCategoriesSelector,
  (categoryId, categories) => categories.find(category => category.id === categoryId)
);

export const getAccessoriesByCategory = createSelector(
  state => state.configurator.accessories.category,
  state => state.configurator.accessories.items,
  state => state.configurator.accessories.sort,
  (selectedCategoryId, accessories, sort) =>
    selectedCategoryId
      ? accessories
          .filter(
            accessory =>
              selectedCategoryId === VIEW_ALL_OPTION_ID ||
              accessory.categoryId === selectedCategoryId ||
              (selectedCategoryId === FEATURED_OPTION_ID && accessory.isFeatured)
          )
          .sort((a, b) => {
            const selectedSortOption = sortOptions.find(sortOption => sortOption.id === sort);

            return selectedSortOption ? selectedSortOption.sortFunction(a, b) : 0;
          })
      : []
);

export const previewAccessorySelector = createSelector(
  state => state.configurator.accessories.previewedAccessory,
  previewedAccessory => previewedAccessory
);

export const completedStepsSelector = createSelector(
  state => state.configurator.completedSteps,
  completedSteps => completedSteps
);

export const getBikeFlipped = createSelector(
  state => state.configurator.flipped,
  flipped => flipped
);

export const getBikeZoomed = createSelector(
  state => state.configurator.zoom,
  zoom => zoom
);

export const getScrollYPosition = createSelector(
  state => state.configurator.scrollYPosition,
  scrollYPosition => scrollYPosition
);

export const getScrollXPosition = createSelector(
  state => state.configurator.scrollXPosition,
  scrollXPosition => scrollXPosition
);

export const getAccessoriesQuery = createSelector(
  state => state.configurator.accessories.query,
  query => query
);

export const getAccessorySort = createSelector(
  state => state.configurator.accessories.sort,
  sort => sort
);

export const getPacks = createSelector(
  state => state.configurator.packs.items,
  state => state.configurator.accessories.items,
  (packs, accessories) =>
    packs.map(pack => ({
      ...pack,
      parts: pack.parts.map(accessoryId => accessories.find(accessory => accessory.id === accessoryId)),
    }))
);

export const getPrintActions = createSelector(
  state => state.configurator.printActions,
  printActions => printActions
);

export const getProvinces = createSelector(
  state => state.configurator.provinces,
  provinces => provinces
);

export const getProvincePricingEnabled = createSelector(
  state => state.configurator.provincePricingEnabled,
  provincePricingEnabled => provincePricingEnabled
);

export const getSelectedProvince = createSelector(
  state => state.configurator.selectedProvince,
  selectedProvince => selectedProvince
);
